import {Component, ElementRef, inject, Input, OnInit, ViewChild} from '@angular/core';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {Card} from '../../../../../shared/components/common/card/card';
import {CreationButtons} from '../../../../../shared/components/ui/creation-buttons/creation-buttons';
import {LabelComponent} from '../../../../../shared/components/form/label/label.component';
import {SelectFieldComponent, SelectOption} from '@shared/components/form/select/select-field.component';
import {TextareaFieldComponent} from '@shared/components/form/textarea/textarea-field.component';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {MatIconButton} from '@angular/material/button';
import {PropuestasComercialesService} from './propuestas-comerciales.service';
import {ErrorService} from '../../../../../shared/services/error.service';
import {
  PropuestaComercial,
  PropuestaComercialFiltros
} from './propuestas-comerciales.interface';
import {DeleteTrigger} from '../../../../../shared/components/ui/delete-trigger/delete-trigger';
import {DatePipe, DecimalPipe} from '@angular/common';
import {SnackbarService} from '@shared/services/snackbar.service';
import {FormsModule} from '@angular/forms';
import {DialogService} from '@shared/services/dialog.service';
import {DetallePropuestaComercial} from './detalle-propuesta-comercial/detalle-propuesta-comercial';
import {SeleccionarContactoPropuesta} from './seleccionar-contacto-propuesta/seleccionar-contacto-propuesta';
import {
  DataTable,
  FilterDate,
  FilterOption,
  FilterSelect,
  FiltersContainer,
  Pagination,
  SearchInput,
  TableCellDirective,
  TableColumn
} from '../../../../../shared/components/ui/list-components';
import {Badge} from '../../../../../shared/components/ui/badge/badge';
import {ButtonComponent} from '../../../../../shared/components/ui/button/button.component';
import {PageHeaderComponent} from '../../../../../shared/components/common/page-header/page-header.component';
import {InputFieldComponent} from '@shared/components/form/input/input-field.component';

@Component({
  selector: 'app-propuestas-comerciales',
  imports: [
    HasPermissionDirective,
    Card,
    CreationButtons,
    LabelComponent,
    MatIcon,
    MatTooltip,
    MatIconButton,
    DeleteTrigger,
    DatePipe,
    DecimalPipe,
    FormsModule,
    Pagination,
    SearchInput,
    FilterSelect,
    FilterDate,
    FiltersContainer,
    DataTable,
    TableCellDirective,
    Badge,
    ButtonComponent,
    DetallePropuestaComercial,
    PageHeaderComponent,
    SelectFieldComponent,
    TextareaFieldComponent,
    InputFieldComponent,
  ],
  templateUrl: './propuestas-comerciales.html'
})
export class PropuestasComerciales implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  @Input() idProspecto!: number;
  @Input() idContacto: number | null = null;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  fileSelected: File | null = null;
  idPropuestaEditar: number | null = null;
  propuestas: PropuestaComercial[] = [];
  loading = true;
  process = false;
  deleting = false;
  deletingIndex: number | null = null;
  downloading = false;
  downloadingIndex: number | null = null;
  descripcion = '';
  presupuestoNro = '';
  monto: number | null = null;
  descuento: number | null = null;
  fechaGeneracion = '';
  fechaEnvio = '';
  estado = 'pendiente';
  estadoCrearOptions: SelectOption[] = [
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'aceptada', label: 'Aceptada' },
    { value: 'rechazada', label: 'Rechazada' },
  ];

  // Paginacion
  currentPage = 1;
  lastPage = 1;
  perPage = 15;
  total = 0;

  // Filtros
  filtros: PropuestaComercialFiltros = {
    busqueda: '',
    estado: '',
    fecha_desde: '',
    fecha_hasta: '',
    fecha_envio_desde: '',
    fecha_envio_hasta: '',
    activo: '',
    fecha_contacto_desde: '',
    fecha_contacto_hasta: ''
  };

  columns: TableColumn[] = [
    { key: 'presupuesto', header: 'Presupuesto' },
    { key: 'contacto_fecha', header: 'Fecha Contacto' },
    { key: 'fechas', header: 'Fechas' },
    { key: 'monto', header: 'Monto' },
    { key: 'estado', header: 'Estado' },
    { key: 'descripcion', header: 'Descripcion' },
    { key: 'activo', header: 'Activo' },
    { key: 'acciones', header: 'Acciones', width: '140px' }
  ];

  estadoOptions: FilterOption[] = [
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'aceptada', label: 'Aceptada' },
    { value: 'rechazada', label: 'Rechazada' }
  ];

  activoOptions: FilterOption[] = [
    { value: 'true', label: 'Activas' },
    { value: 'false', label: 'Inactivas' }
  ];

  private propuestasComercialesService = inject(PropuestasComercialesService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogService = inject(DialogService);

  ngOnInit(): void {
    this.getPropuestasComerciales();
  }

  getPropuestasComerciales(page: number = 1): void {
    this.loading = true;
    const filtrosConProspecto = {
      ...this.filtros,
      crm_prospecto_id: this.idProspecto
    };
    this.propuestasComercialesService.index(page, this.perPage, filtrosConProspecto).subscribe({
      next: (response) => {
        if (response.status) {
          const payload: any = response.data;
          const rawData = Array.isArray(payload.data)
            ? payload.data
            : payload.data?.data ?? [];
          const pagination = payload.pagination ?? payload.data?.pagination;

          this.propuestas = rawData.map((item: any) => ({
            ...item,
            fecha_generacion_presupuesto: item.fecha_generacion_presupuesto ?? item.fecha_generacion ?? null,
            fecha_envio_presupuesto: item.fecha_envio_presupuesto ?? item.fecha_envio ?? null
          }));

          if (pagination) {
            this.currentPage = pagination.current_page;
            this.lastPage = pagination.last_page;
            this.total = pagination.total;
          } else {
            this.currentPage = 1;
            this.lastPage = 1;
            this.total = this.propuestas.length;
          }
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (file.type !== 'application/pdf') {
      this.snackbarService.showSnackBar('Solo se permiten archivos PDF', 'error');
      this.clearFileInput();
      return;
    }

    this.fileSelected = file;
  }

  create(): void {
    if (!this.idContacto) return;

    this.process = true;
    const formData = new FormData();

    formData.append('crm_contacto_id', this.idContacto.toString());
    formData.append('crm_prospecto_id', this.idProspecto.toString());

    if (this.fileSelected) formData.append('path', this.fileSelected);
    if (this.descripcion) formData.append('descripcion', this.descripcion);
    if (this.presupuestoNro) formData.append('presupuesto_nro', this.presupuestoNro);
    if (this.monto !== null) formData.append('monto', this.monto.toString());
    if (this.descuento !== null) formData.append('descuento', this.descuento.toString());
    if (this.fechaGeneracion) formData.append('fecha_generacion_presupuesto', this.fechaGeneracion);
    if (this.fechaEnvio) formData.append('fecha_envio_presupuesto', this.fechaEnvio);
    if (this.estado) formData.append('estado', this.estado);

    this.propuestasComercialesService.store(formData).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Propuesta comercial creada correctamente');
          this.resetForm();
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.process = false;
    });
  }

  resetForm(): void {
    this.clearFileInput();
    this.idContacto = null;
    this.descripcion = '';
    this.presupuestoNro = '';
    this.monto = null;
    this.descuento = null;
    this.fechaGeneracion = '';
    this.fechaEnvio = '';
    this.estado = 'pendiente';
    this.getPropuestasComerciales();
  }

  clearFileInput(): void {
    this.fileSelected = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  delete(id: number, index: number): void {
    this.deleting = true;
    this.deletingIndex = index;

    this.propuestasComercialesService.destroy(id).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Propuesta comercial eliminada correctamente');
          this.getPropuestasComerciales();
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.deleting = false;
      this.deletingIndex = null;
    });
  }

  download(id: number, index: number): void {
    this.downloading = true;
    this.downloadingIndex = index;

    this.propuestasComercialesService.download(id).subscribe({
      next: (response) => {
        this.snackbarService.showSnackBar('Se está preparando la descarga');
        const fileURL = URL.createObjectURL(response);
        window.open(fileURL, '_blank');
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.downloading = false;
      this.downloadingIndex = null;
    });
  }

  edit(idPropuesta: number): void {
    this.idPropuestaEditar = idPropuesta;
  }

  onDetalleClose(updated: boolean): void {
    this.idPropuestaEditar = null;
    if (updated) {
      this.getPropuestasComerciales();
    }
  }

  openSeleccionarContacto(): void {
    this.dialogService.openForm(SeleccionarContactoPropuesta, {
      size: 'xl',
      config: { height: '90vh', maxHeight: '90vh' },
      data: { idProspecto: this.idProspecto }
    }).afterClosed().subscribe((contactoId) => {
      if (contactoId) {
        this.prepareNewPropuesta(contactoId as number);
      }
    });
  }

  private prepareNewPropuesta(contactoId: number): void {
    this.clearFileInput();
    this.idContacto = contactoId;
    this.descripcion = '';
    this.presupuestoNro = '';
    this.monto = null;
    this.descuento = null;
    this.fechaGeneracion = '';
    this.fechaEnvio = '';
    this.estado = 'pendiente';
  }

  onSearchChange(value: string): void {
    this.filtros.busqueda = value;
    this.currentPage = 1;
    this.getPropuestasComerciales();
  }

  onEstadoChange(value: string | number): void {
    this.filtros.estado = value as string;
    this.currentPage = 1;
    this.getPropuestasComerciales();
  }

  onFechaDesdeChange(value: string): void {
    this.filtros.fecha_desde = value;
    this.currentPage = 1;
    this.getPropuestasComerciales();
  }

  onFechaHastaChange(value: string): void {
    this.filtros.fecha_hasta = value;
    this.currentPage = 1;
    this.getPropuestasComerciales();
  }

  onFechaEnvioDesdeChange(value: string): void {
    this.filtros.fecha_envio_desde = value;
    this.currentPage = 1;
    this.getPropuestasComerciales();
  }

  onFechaEnvioHastaChange(value: string): void {
    this.filtros.fecha_envio_hasta = value;
    this.currentPage = 1;
    this.getPropuestasComerciales();
  }

  onActivoChange(value: string | number): void {
    this.filtros.activo = value as string;
    this.currentPage = 1;
    this.getPropuestasComerciales();
  }

  onFechaContactoDesdeChange(value: string): void {
    this.filtros.fecha_contacto_desde = value;
    this.currentPage = 1;
    this.getPropuestasComerciales();
  }

  onFechaContactoHastaChange(value: string): void {
    this.filtros.fecha_contacto_hasta = value;
    this.currentPage = 1;
    this.getPropuestasComerciales();
  }

  get hasActiveFilters(): boolean {
    return Object.values(this.filtros).some(valor => valor !== null && valor !== undefined && valor !== '');
  }

  clearFilters(): void {
    this.filtros = {
      busqueda: '',
      estado: '',
      fecha_desde: '',
      fecha_hasta: '',
      fecha_envio_desde: '',
      fecha_envio_hasta: '',
      activo: '',
      fecha_contacto_desde: '',
      fecha_contacto_hasta: ''
    };
    this.currentPage = 1;
    this.getPropuestasComerciales();
  }

  onPageChange(page: number): void {
    this.getPropuestasComerciales(page);
  }

  onPerPageChange(perPage: number): void {
    this.perPage = perPage;
    this.currentPage = 1;
    this.getPropuestasComerciales(1);
  }

  onRowClick(event: { item: PropuestaComercial; index: number }): void {
    this.edit(event.item.id);
  }

  getArchivoNombre(path: string | null): string {
    if (!path) return 'Sin archivo';
    const normalized = path.replace(/\\/g, '/');
    const parts = normalized.split('/');
    return parts[parts.length - 1] || path;
  }
}
